import { ReunionAgency, ReunionTransaction } from '../hooks/useReunionMensuelleData';
import { Scope, ScopeColumn, getColumnForTransaction } from '../components/settings/reunion-mensuelle/ScopeSelector';
import { ExcelColDef, ExcelRowDef, ExcelSheetDef, downloadMultiSheetExcel } from './excelExport';
import {
  PeriodBucket,
  diffDays,
  getPeriodKeyForDate,
  buildPeriodColumnAverages,
  buildPeriodCategoryCounts,
  buildPeriodMultiCategoryCounts,
  getPriceBracket,
  PRICE_BRACKET_LABELS,
} from './reunionMensuelle';

const PROPERTY_TYPES = ['Appartement', 'Maison', 'Immeuble', 'Box', 'Terrain', 'Parking'];

const RETROCESSION_CATEGORIES: { key: string; label: string; predicate: (tx: ReunionTransaction) => boolean }[] = [
  { key: 'newco', label: 'Newco', predicate: tx => tx.property_origin === 'newco' },
  { key: 'ks', label: 'KS', predicate: tx => tx.property_origin === 'syndic' },
  { key: 'intercabinet', label: 'Intercabinet', predicate: tx => tx.retrocession_types.includes('intercabinet') },
  { key: 'recommandation', label: 'Recommandation', predicate: tx => tx.retrocession_types.includes('recommandation_interne') },
  { key: 'lefeuvre', label: 'Rétrocession Lefeuvre', predicate: tx => tx.retrocession_types.includes('retrocession_lefeuvre') },
];

export interface ReunionExportSections {
  delai: boolean;
  ventes: boolean;
  honoraires: boolean;
  retrocession: boolean;
  interagence: boolean;
}

export interface ReunionExportOptions {
  sections: ReunionExportSections;
  includeDetails: boolean;
}

function periodColumns(columns: ScopeColumn[]): ExcelColDef[] {
  return [
    { header: 'Période', key: 'periode', width: 22 },
    ...columns.map(c => ({ header: c.label, key: c.key, width: 16, align: 'right' as const })),
  ];
}

function sectionTitleRow(columns: ExcelColDef[], label: string): ExcelRowDef {
  const data: Record<string, string> = { periode: label };
  return { data, isSection: true, level: 0 };
}

function matrixRows(
  periods: PeriodBucket[],
  columnKeys: string[],
  data: Map<string, Map<string, number | null>>,
  formatValue: (v: number) => string,
): ExcelRowDef[] {
  return periods.map(p => {
    const row = data.get(p.key);
    const out: Record<string, string | number> = { periode: p.label };
    columnKeys.forEach(key => {
      const v = row?.get(key);
      out[key] = v === null || v === undefined ? '—' : formatValue(v);
    });
    return { data: out };
  });
}

function buildDelaiSheet(
  transactions: ReunionTransaction[],
  periods: PeriodBucket[],
  columns: ScopeColumn[],
  scope: Scope,
): ExcelSheetDef {
  const columnKeys = columns.map(c => c.key);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const getColumnKey = (tx: ReunionTransaction) => getColumnForTransaction(tx, scope);
  const cols = periodColumns(columns);
  const formatDays = (v: number) => Math.round(v).toString();

  const mandatToCompromis = buildPeriodColumnAverages(
    transactions, periods, columnKeys, getPeriodKey, getColumnKey,
    tx => diffDays(tx.compromise_date, tx.mandate_date)
  );
  const compromisToActePrevi = buildPeriodColumnAverages(
    transactions, periods, columnKeys, getPeriodKey, getColumnKey,
    tx => diffDays(tx.expected_acte_date, tx.compromise_date)
  );
  const compromisToActeSigne = buildPeriodColumnAverages(
    transactions, periods, columnKeys, getPeriodKey, getColumnKey,
    tx => diffDays(tx.acte_date, tx.compromise_date)
  );

  const rows: ExcelRowDef[] = [
    sectionTitleRow(cols, 'Mandat → Compromis (jours)'),
    ...matrixRows(periods, columnKeys, mandatToCompromis, formatDays),
    sectionTitleRow(cols, 'Compromis → Acte prévisionnel (jours)'),
    ...matrixRows(periods, columnKeys, compromisToActePrevi, formatDays),
    sectionTitleRow(cols, 'Compromis → Acte signé (jours)'),
    ...matrixRows(periods, columnKeys, compromisToActeSigne, formatDays),
  ];

  return { name: 'Délai', columns: cols, rows };
}

function buildVentesSheets(
  transactions: ReunionTransaction[],
  periods: PeriodBucket[],
  scope: Scope,
): ExcelSheetDef[] {
  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const formatCount = (v: number) => v.toString();
  const formatEuros = (v: number) => Math.round(v).toLocaleString('fr-FR');

  const typologieCols: ExcelColDef[] = [
    { header: 'Période', key: 'periode', width: 22 },
    ...PROPERTY_TYPES.map(t => ({ header: t, key: t, width: 14, align: 'right' as const })),
  ];
  const byTypologie = buildPeriodCategoryCounts(inScope, periods, PROPERTY_TYPES, getPeriodKey, tx => tx.property_type);
  const avgPriceByTypologie = buildPeriodColumnAverages(
    inScope, periods, PROPERTY_TYPES, getPeriodKey, tx => tx.property_type, tx => tx.sale_price
  );
  const typologieRows: ExcelRowDef[] = [
    sectionTitleRow(typologieCols, 'Nombre de ventes'),
    ...matrixRows(periods, PROPERTY_TYPES, byTypologie as Map<string, Map<string, number | null>>, formatCount),
    sectionTitleRow(typologieCols, 'Prix net vendeur moyen (€)'),
    ...matrixRows(periods, PROPERTY_TYPES, avgPriceByTypologie, formatEuros),
  ];

  const trancheCols: ExcelColDef[] = [
    { header: 'Période', key: 'periode', width: 22 },
    ...PRICE_BRACKET_LABELS.map(t => ({ header: t, key: t, width: 14, align: 'right' as const })),
  ];
  const byTranche = buildPeriodCategoryCounts(inScope, periods, PRICE_BRACKET_LABELS, getPeriodKey, tx => getPriceBracket(tx.sale_price));
  const trancheRows: ExcelRowDef[] = [
    sectionTitleRow(trancheCols, 'Nombre de ventes'),
    ...matrixRows(periods, PRICE_BRACKET_LABELS, byTranche as Map<string, Map<string, number | null>>, formatCount),
  ];

  return [
    { name: 'Ventes - Typologie', columns: typologieCols, rows: typologieRows },
    { name: 'Ventes - Tranche prix', columns: trancheCols, rows: trancheRows },
  ];
}

function buildHonorairesSheet(
  transactions: ReunionTransaction[],
  periods: PeriodBucket[],
  columns: ScopeColumn[],
  scope: Scope,
): ExcelSheetDef {
  const columnKeys = columns.map(c => c.key);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const getColumnKey = (tx: ReunionTransaction) => getColumnForTransaction(tx, scope);
  const cols = periodColumns(columns);
  const formatEuros = (v: number) => Math.round(v).toLocaleString('fr-FR');

  const averages = buildPeriodColumnAverages(transactions, periods, columnKeys, getPeriodKey, getColumnKey, tx => tx.fees_without_tax);

  const rows: ExcelRowDef[] = [
    sectionTitleRow(cols, "Moyenne d'honoraires HT (€)"),
    ...matrixRows(periods, columnKeys, averages, formatEuros),
  ];

  return { name: "Moyenne d'honoraires", columns: cols, rows };
}

function buildRetrocessionSheet(
  transactions: ReunionTransaction[],
  periods: PeriodBucket[],
  scope: Scope,
): ExcelSheetDef {
  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const formatCount = (v: number) => v.toString();

  const categoryKeys = RETROCESSION_CATEGORIES.map(c => c.key);
  const cols: ExcelColDef[] = [
    { header: 'Période', key: 'periode', width: 22 },
    ...RETROCESSION_CATEGORIES.map(c => ({ header: c.label, key: c.key, width: 18, align: 'right' as const })),
  ];
  const counts = buildPeriodMultiCategoryCounts(inScope, periods, RETROCESSION_CATEGORIES, getPeriodKey);

  const rows: ExcelRowDef[] = [
    sectionTitleRow(cols, 'Nombre de dossiers'),
    ...matrixRows(periods, categoryKeys, counts, formatCount),
  ];

  return { name: 'Rétrocession', columns: cols, rows };
}

function buildInterAgenceSheets(
  transactions: ReunionTransaction[],
  agencies: ReunionAgency[],
  periods: PeriodBucket[],
  columns: ScopeColumn[],
  scope: Scope,
): ExcelSheetDef[] {
  const agencyToCompany = new Map(agencies.map(a => [a.name, a.company]));
  const isInterAgenceInterne = (tx: ReunionTransaction): boolean => {
    if (!tx.listing_agency || !tx.buyer_agency) return false;
    if (tx.listing_agency === tx.buyer_agency) return false;
    const listingCompany = agencyToCompany.get(tx.listing_agency);
    const buyerCompany = agencyToCompany.get(tx.buyer_agency);
    return !!listingCompany && listingCompany === buyerCompany;
  };

  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const interAgenceTx = inScope.filter(isInterAgenceInterne);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const formatCount = (v: number) => v.toString();

  const categories = columns.map(c => ({
    key: c.key,
    label: c.label,
    predicate: (tx: ReunionTransaction) => isInterAgenceInterne(tx) && getColumnForTransaction(tx, scope) === c.key,
  }));
  const counts = buildPeriodMultiCategoryCounts(inScope, periods, categories, getPeriodKey);
  const cols = periodColumns(columns);
  const rows: ExcelRowDef[] = [
    sectionTitleRow(cols, 'Dossiers en inter-agence interne'),
    ...matrixRows(periods, columns.map(c => c.key), counts, formatCount),
  ];

  const stats = new Map<string, { asListing: number; asBuyer: number }>();
  interAgenceTx.forEach(tx => {
    const l = tx.listing_agency!;
    const b = tx.buyer_agency!;
    stats.set(l, { asListing: (stats.get(l)?.asListing ?? 0) + 1, asBuyer: stats.get(l)?.asBuyer ?? 0 });
    stats.set(b, { asListing: stats.get(b)?.asListing ?? 0, asBuyer: (stats.get(b)?.asBuyer ?? 0) + 1 });
  });
  const ranking = [...stats.entries()]
    .map(([agency, s]) => ({ agency, ...s, total: s.asListing + s.asBuyer }))
    .sort((a, b) => b.total - a.total);

  const rankingCols: ExcelColDef[] = [
    { header: 'Agence', key: 'agency', width: 24 },
    { header: 'Vendeur', key: 'asListing', width: 14, align: 'right' },
    { header: 'Apporteur', key: 'asBuyer', width: 14, align: 'right' },
    { header: 'Total', key: 'total', width: 14, align: 'right' },
  ];
  const rankingRows: ExcelRowDef[] = ranking.map(r => ({
    data: { agency: r.agency, asListing: r.asListing, asBuyer: r.asBuyer, total: r.total },
  }));

  return [
    { name: 'Inter-agence', columns: cols, rows },
    { name: 'Inter-agence - Classement', columns: rankingCols, rows: rankingRows },
  ];
}

function buildDetailSheet(
  transactions: ReunionTransaction[],
  periods: PeriodBucket[],
  columns: ScopeColumn[],
  scope: Scope,
): ExcelSheetDef {
  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);
  const columnLabels = new Map(columns.map(c => [c.key, c.label]));

  const sorted = [...inScope].sort((a, b) => (a.compromise_date ?? '').localeCompare(b.compromise_date ?? ''));

  const cols: ExcelColDef[] = [
    { header: 'Période', key: 'periode', width: 20 },
    { header: 'Société / Agence', key: 'colonne', width: 20 },
    { header: 'N° bien', key: 'property_number', width: 14 },
    { header: 'Vendeur', key: 'seller', width: 20 },
    { header: 'Acquéreur', key: 'buyer', width: 20 },
    { header: 'Typologie', key: 'property_type', width: 14 },
    { header: 'Prix net vendeur', key: 'sale_price', width: 16, numFmt: '#,##0 €', align: 'right' },
    { header: 'Honoraires HT', key: 'fees_without_tax', width: 16, numFmt: '#,##0 €', align: 'right' },
    { header: 'Mandat', key: 'mandate_date', width: 14, align: 'right' },
    { header: 'Compromis', key: 'compromise_date', width: 14, align: 'right' },
    { header: 'Acte prévi.', key: 'expected_acte_date', width: 14, align: 'right' },
    { header: 'Acte signé', key: 'acte_date', width: 14, align: 'right' },
    { header: 'Statut', key: 'status', width: 14 },
  ];

  const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

  const rows: ExcelRowDef[] = sorted.map(tx => {
    const periodKey = getPeriodKey(tx);
    const period = periods.find(p => p.key === periodKey);
    const columnKey = getColumnForTransaction(tx, scope);
    return {
      data: {
        periode: period?.label ?? '—',
        colonne: (columnKey && columnLabels.get(columnKey)) ?? columnKey ?? '—',
        property_number: tx.property_number ?? '—',
        seller: tx.seller ?? '—',
        buyer: tx.buyer ?? '—',
        property_type: tx.property_type ?? '—',
        sale_price: tx.sale_price ?? '',
        fees_without_tax: tx.fees_without_tax ?? '',
        mandate_date: formatDate(tx.mandate_date),
        compromise_date: formatDate(tx.compromise_date),
        expected_acte_date: formatDate(tx.expected_acte_date),
        acte_date: formatDate(tx.acte_date),
        status: tx.status,
      },
    };
  });

  return { name: 'Détail transactions', columns: cols, rows };
}

export async function exportReunionMensuelle(
  transactions: ReunionTransaction[],
  agencies: ReunionAgency[],
  periods: PeriodBucket[],
  columns: ScopeColumn[],
  scope: Scope,
  fiscalYear: number,
  options: ReunionExportOptions,
): Promise<void> {
  const sheets: ExcelSheetDef[] = [];

  if (options.sections.delai) sheets.push(buildDelaiSheet(transactions, periods, columns, scope));
  if (options.sections.ventes) sheets.push(...buildVentesSheets(transactions, periods, scope));
  if (options.sections.honoraires) sheets.push(buildHonorairesSheet(transactions, periods, columns, scope));
  if (options.sections.retrocession) sheets.push(buildRetrocessionSheet(transactions, periods, scope));
  if (options.sections.interagence) sheets.push(...buildInterAgenceSheets(transactions, agencies, periods, columns, scope));

  if (options.includeDetails) sheets.push(buildDetailSheet(transactions, periods, columns, scope));

  if (sheets.length === 0) return;

  const filename = `reunion-mensuelle_${fiscalYear}-${fiscalYear + 1}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  await downloadMultiSheetExcel(filename, sheets);
}
